import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, of, timeout } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserAuthService } from 'src/app/shared/services/user-auth.service';

/**
 * Upper bound on how long we wait for MSAL to settle before falling back to an immediate
 * decision. Only reached when MSAL is genuinely broken — an unbounded wait would leave the
 * user staring at a spinner forever, which is worse than sending them to the landing page.
 */
const MSAL_READY_TIMEOUT_MS = 15000;

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private readonly router: Router,
    private readonly userAuthService: UserAuthService,
    private readonly msalBroadcastService: MsalBroadcastService
  ) {}

  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    /* AD auth is cookie-based and never involves MSAL — decide immediately so AD users are
       never gated on an MSAL signal. */
    if (this.userAuthService.hasADCredentials()) {
      return true;
    }

    /* Wait for MSAL to finish initialization and redirect handling before deciding.
       Reading the account cache while inProgress$ is still Startup/HandleRedirect can report
       an authenticated user as signed out and bounce them to /landing. */
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      timeout({
        first: MSAL_READY_TIMEOUT_MS,
        with: () => of(InteractionStatus.None),
      }),
      map(() => this.resolveAccess())
    );
  }

  private resolveAccess(): boolean | UrlTree {
    if (this.userAuthService.isAuthenticated()) {
      return true;
    }

    /* Send former AD users back to their own sign-in page rather than the MSAL landing. */
    this.router.navigate([this.userAuthService.wasAdSession() ? '/ad-login' : '/landing']);
    return false;
  }
}
